import { HTMLAttributes, useEffect, useState } from 'react';

import { Alert, LoadingBubbles, Severity } from '@scaleaq/scaleui';

import { Icon } from '@/utils';

import { AlertWrapper, LoadingWrapper, StyledIconButton } from './feedbackAlert.styles.ts';

export type FeedbackAlertProps = HTMLAttributes<HTMLDivElement> & {
  text?: string;
  variant?: Severity;
  loading?: boolean;
  speed?: number;
  duration?: number;
  /** Fired after auto-hide or close; clear parent `feedback` state so an identical message can show again. */
  onDismiss?: () => void;
};

export const FeedbackAlert = ({
  text,
  variant,
  loading,
  speed = 1500,
  duration = 10000,
  onDismiss,
  ...rest
}: FeedbackAlertProps) => {
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    if (loading) {
      return;
    }

    setShowAlert(true);

    if (!text || !variant) {
      return;
    }

    const timer = setTimeout(() => {
      setShowAlert(false);
      onDismiss?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, loading, onDismiss, text, variant]);

  if (loading) {
    return (
      <LoadingWrapper alignment='center' {...rest}>
        <LoadingBubbles size='medium' />
      </LoadingWrapper>
    );
  }
  if (showAlert && text && variant) {
    return (
      <AlertWrapper alignment='center' $speed={speed} {...rest}>
        <Alert
          text={text}
          severity={variant}
          action={
            <StyledIconButton
              $feedbackVariant={variant}
              size='tiny'
              color='neutral'
              variant='ghost'
              Icon={<Icon.X size='large' />}
              onClick={() => {
                setShowAlert(false);
                onDismiss?.();
              }}
            />
          }
        />
      </AlertWrapper>
    );
  }

  return null;
};
